import React from 'react';
import { RiCloseLine } from '@remixicon/react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle
} from '@components/ui/Drawer';
import { formatPrice } from '@/utils';
import { getAircraftModelName } from '@/features/tails/utils';

/**
 * PriceBreakdownDrawer Component
 * Shows per-aircraft price breakdown in a right drawer
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the drawer is open
 * @param {Function} props.onClose - Callback to close the drawer
 * @param {Array} props.flightOptions - Array of bid options with price data
 */
const PriceBreakdownDrawer = ({ isOpen, onClose, flightOptions: flightOptionsProp }) => {
  const flightOptions = flightOptionsProp || [];
  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent side="right" className="w-full !max-w-2xl flex flex-col">
        <DrawerHeader>
          <DrawerTitle className="text-xl">Price Breakdown</DrawerTitle>
        </DrawerHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-2 pb-8">
          <div className="space-y-4">
            {flightOptions.map((option, index) => {
              const operatorCost = parseFloat(option.total_price) || 0;
              const brokerMarkup = parseFloat(option.broker_markup) || 0;
              const fet = parseFloat(option.federal_excise_tax) || 0;
              const segmentTax = parseFloat(option.segment_tax) || 0;
              const platformFee = parseFloat(option.platform_fee) || 0;
              const totalCost = parseFloat(option.final_price) || 0;

              const aircraftName = getAircraftModelName(option.aircraft) || `Aircraft ${index + 1}`;

              return (
                <div
                  key={option.bid_option_id || option.aircraft_id || index}
                  className="border border-gray-200 rounded-xl p-5"
                >
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                    {aircraftName}
                  </h3>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Operator Cost</span>
                      <span className="text-sm font-medium text-gray-900">{formatPrice(operatorCost)}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Broker Margin</span>
                      <span className="text-sm font-medium text-gray-900">{formatPrice(brokerMarkup)}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Federal Excise Tax (7.5%)</span>
                      <span className="text-sm font-medium text-gray-900">{formatPrice(fet)}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">International or Segment Tax</span>
                      <span className="text-sm font-medium text-gray-900">{formatPrice(segmentTax)}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">TrustJet Fee (1.5%)</span>
                      <span className="text-sm font-medium text-gray-900">{formatPrice(platformFee)}</span>
                    </div>

                    <div className="border-t border-gray-200 pt-3 mt-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-900">Total Cost</span>
                        <span className="text-sm text-gray-900 font-medium">{formatPrice(totalCost)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Card fee note */}
          {flightOptions.length > 0 && (
            <div className="mt-4 bg-gray-50 rounded-xl px-4 py-3">
              <p className="text-xs text-gray-500">
                If client chooses to pay by card, a 4% card fee will be added on top of the total cost.
              </p>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default PriceBreakdownDrawer;
